import dayjs from 'dayjs';

/**
 * Textos del centro de incidentes. Los valores (`value`) son los que devuelve el API
 * del backend (`GET /incidents`); aquí solo se traducen.
 *
 * No hay «abierto» ni «resuelto»: el centro solo AVISA. Nada cierra un incidente, así
 * que lo que importa es cuándo se detectó y si ya lo viste.
 */

export const INTEGRATIONS = [
  { value: 'sms', label: 'SMS' },
  { value: 'templates-meta', label: 'Templates de Meta' },
  // Las dos son la misma central telefónica y sus troncales: una falla de troncal afecta
  // por igual a las campañas y a las llamadas normales, así que se muestran como una sola.
  { value: 'call-blasting', label: 'Telefonía y Call Blasting' },
  { value: 'telefonia', label: 'Telefonía y Call Blasting' },
  { value: 'uxxi', label: 'UXXI' },
  { value: 'cartera-umb', label: 'Cartera (UMB)' },
];

/**
 * Lo que se ve en las tarjetas y en el filtro: una entrada por grupo. `members` son los
 * valores reales del backend (`integration`) que caen en ese grupo. Todas las demás
 * integraciones son un grupo de un solo miembro.
 */
export const INTEGRATION_GROUPS = [
  { value: 'sms', label: 'SMS', members: ['sms'] },
  { value: 'templates-meta', label: 'Templates de Meta', members: ['templates-meta'] },
  {
    value: 'telefonia-call-blasting',
    label: 'Telefonía y Call Blasting',
    members: ['call-blasting', 'telefonia'],
  },
  { value: 'uxxi', label: 'UXXI', members: ['uxxi'] },
  { value: 'cartera-umb', label: 'Cartera (UMB)', members: ['cartera-umb'] },
];

/**
 * Integraciones reales del backend que cubre un valor del filtro. Un valor que no es un
 * grupo (o es vacío) se trata como una integración suelta, para no esconder nada.
 */
export const groupMembers = (value) => {
  if (!value) return [];
  const group = INTEGRATION_GROUPS.find((item) => item.value === value);
  return group ? group.members : [value];
};

export const CAUSES = [
  { value: 'credenciales', label: 'Credenciales inválidas o vencidas' },
  { value: 'permisos', label: 'El proveedor negó permisos' },
  { value: 'pago', label: 'Pago pendiente en el proveedor' },
  { value: 'cuenta-restringida', label: 'Cuenta o número restringido' },
  { value: 'registro', label: 'Registro o verificación pendiente' },
  { value: 'sin-envios', label: 'Ningún envío sale bien' },
  { value: 'servicio-caido', label: 'El servicio no responde' },
  { value: 'troncal-caida', label: 'Troncal caída o sin registro' },
  { value: 'troncal-con-fallas', label: 'Troncal con fallas (red u operador)' },
  { value: 'central-sin-capacidad', label: 'Central sin capacidad (límite de archivos abiertos)' },
  { value: 'ejecuciones-fallidas', label: 'Las últimas ejecuciones fallaron' },
  { value: 'otra', label: 'Otra causa' },
];

/** Qué le pasó al incidente con cada aviso (campo `change` de las muestras). */
const CHANGE_LABELS = {
  opened: 'Se detectó el incidente',
  watching: 'Fallo anterior, aún no era un incidente',
  updated: 'Nuevo fallo',
  resolved: 'Nuevo fallo',
  unchanged: 'Sin cambios',
};

export const changeLabel = (value) => CHANGE_LABELS[value] || value || '—';

/** Cliente `GLOBAL`: lo que afecta a todos (p. ej. una central telefónica caída). */
export const GLOBAL_CLIENT = 'GLOBAL';

const labelOf = (list, value) =>
  list.find((item) => item.value === value)?.label || value || '—';

export const integrationLabel = (value) => labelOf(INTEGRATIONS, value);
export const causeLabel = (value) => labelOf(CAUSES, value);

/** Nombre del cliente si se conoce; si no, `—`. El código se muestra aparte, siempre. */
export const clientName = (clientId, names = {}) => {
  if (!clientId) return '—';
  if (clientId === GLOBAL_CLIENT) return 'Todos los clientes';
  return names[String(clientId)] || '—';
};

/** Nombre y código juntos: «Siigo · 2353964». Sin nombre, solo el código. */
export const clientLabel = (clientId, names = {}) => {
  if (!clientId) return '—';
  if (clientId === GLOBAL_CLIENT) return 'Todos los clientes';
  const name = names[String(clientId)];
  return name ? `${name} · ${clientId}` : String(clientId);
};

/** Cuándo se detectó: cuando se abrió; si no (aún en vigilancia), la primera vez que falló. */
export const detectedAt = (incident) => incident?.openedAt || incident?.firstSeenAt || null;

/**
 * ¿Es nuevo para quien mira? Lo es si se detectó DESPUÉS de lo último que vio.
 * Sin una última visita (primera vez), todo es nuevo.
 */
export const isNewIncident = (incident, lastSeenAt) => {
  const detected = Date.parse(detectedAt(incident) || '');
  if (Number.isNaN(detected)) return false;
  if (!lastSeenAt) return true;
  const seen = Date.parse(lastSeenAt);
  return Number.isNaN(seen) ? true : detected > seen;
};

export const formatDateTime = (iso) => {
  if (!iso) return '—';
  const date = dayjs(iso);
  return date.isValid() ? date.format('DD MMM YYYY, HH:mm') : '—';
};

/**
 * «hace 5 min», «hace 3 h», «hace 2 d». Sin librerías extra: solo se necesita
 * un orden de magnitud para saber si algo es reciente.
 */
export const relativeTime = (iso, now = Date.now()) => {
  if (!iso) return '—';
  const ms = now - Date.parse(iso);
  if (Number.isNaN(ms)) return '—';
  if (ms < 60_000) return 'hace un momento';
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  return `hace ${Math.floor(hours / 24)} d`;
};

/** Una muestra del backend como una línea: «503 · El proveedor respondio 503». */
export const sampleSummary = (sample) => {
  if (!sample) return '—';
  const parts = [sample.code != null ? String(sample.code) : '', sample.reason || '']
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
};

export const stringifyJson = (value) => {
  if (value === undefined || value === null) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};
